import React, { useEffect, useState } from 'react';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import TextField from 'material-ui/TextField';
import Checkbox from 'material-ui/Checkbox';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getCorpus, listCorpora, listModels, startSuite } from 'services/testRuns';
import { pinnedGazetteer } from './metrics';

const DEFAULTS = {
    replicas: 1,
    timeout: 40,
    memoryReservationGb: 6,
    japeLimitsEnabled: false,
    japeTrackAnnotations: true,
    skipFailedCheck: false,
};

function optionNumber(value, fallback) {
    return String(value !== undefined && value !== null ? value : fallback);
}

function imageItems(list, current) {
    const items = Array.isArray(list) ? list.slice() : [];
    if (current && !items.some(item => item.image === current)) {
        items.unshift({ image: current });
    }
    return items;
}

export default function StartDialog({ open, onClose, onStarted, source }) {
    const queryClient = useQueryClient();
    const sourceId = source && (source.id || source._id);
    const [corpusId, setCorpusId] = useState('');
    const [cargoImageTag, setCargoImageTag] = useState('');
    const [vesselImageTag, setVesselImageTag] = useState('');
    const [gazetteerRevision, setGazetteerRevision] = useState('');
    const [reason, setReason] = useState('');
    const [replicas, setReplicas] = useState(String(DEFAULTS.replicas));
    const [timeout, setTimeoutSec] = useState(String(DEFAULTS.timeout));
    const [memoryReservationGb, setMemoryReservationGb] = useState(String(DEFAULTS.memoryReservationGb));
    const [japeLimitsEnabled, setJapeLimitsEnabled] = useState(DEFAULTS.japeLimitsEnabled);
    const [japeTrackAnnotations, setJapeTrackAnnotations] = useState(DEFAULTS.japeTrackAnnotations);
    const [skipFailedCheck, setSkipFailedCheck] = useState(DEFAULTS.skipFailedCheck);
    const [error, setError] = useState('');

    const corpora = useQuery(['test-run-corpora', open], listCorpora, { enabled: open });
    const cargoModels = useQuery(['test-run-models', 'cargo', open], () => listModels('cargo'), { enabled: open });
    const vesselModels = useQuery(['test-run-models', 'vessel', open], () => listModels('vessel'), { enabled: open });
    const corpus = useQuery(['bbr-corpus', corpusId], () => getCorpus(corpusId), { enabled: open && Boolean(corpusId) });

    useEffect(() => {
        if (!open) {
            return;
        }
        setError('');
        if (source) {
            const options = source.options || {};
            setCorpusId(source.corpusId || '');
            setCargoImageTag(source.cargoImageTag || '');
            setVesselImageTag(source.vesselImageTag || '');
            setGazetteerRevision(pinnedGazetteer(source));
            setReason(source.reason || '');
            setReplicas(optionNumber(options.replicas, DEFAULTS.replicas));
            setTimeoutSec(optionNumber(options.timeout, DEFAULTS.timeout));
            setMemoryReservationGb(optionNumber(options.memoryReservationGb, DEFAULTS.memoryReservationGb));
            setJapeLimitsEnabled(Boolean(
                options.japeLimitsEnabled !== undefined ? options.japeLimitsEnabled : DEFAULTS.japeLimitsEnabled,
            ));
            setJapeTrackAnnotations(
                options.japeTrackAnnotations !== undefined
                    ? Boolean(options.japeTrackAnnotations)
                    : DEFAULTS.japeTrackAnnotations,
            );
            setSkipFailedCheck(Boolean(
                options.skipFailedCheck !== undefined ? options.skipFailedCheck : DEFAULTS.skipFailedCheck,
            ));
            return;
        }
        setCorpusId('');
        setCargoImageTag('');
        setVesselImageTag('');
        setGazetteerRevision('');
        setReason('');
        setReplicas(String(DEFAULTS.replicas));
        setTimeoutSec(String(DEFAULTS.timeout));
        setMemoryReservationGb(String(DEFAULTS.memoryReservationGb));
        setJapeLimitsEnabled(DEFAULTS.japeLimitsEnabled);
        setJapeTrackAnnotations(DEFAULTS.japeTrackAnnotations);
        setSkipFailedCheck(DEFAULTS.skipFailedCheck);
    }, [open, sourceId]);

    useEffect(() => {
        if (!open || sourceId || !corpus.data || corpus.data._id !== corpusId) {
            return;
        }
        const options = corpus.data.options || {};
        setReplicas(optionNumber(options.replicas, DEFAULTS.replicas));
        setTimeoutSec(optionNumber(options.timeout, DEFAULTS.timeout));
        setMemoryReservationGb(optionNumber(options.memoryReservationGb, DEFAULTS.memoryReservationGb));
        setJapeLimitsEnabled(Boolean(
            options.japeLimitsEnabled !== undefined ? options.japeLimitsEnabled : DEFAULTS.japeLimitsEnabled,
        ));
        setJapeTrackAnnotations(
            options.japeTrackAnnotations !== undefined
                ? Boolean(options.japeTrackAnnotations)
                : DEFAULTS.japeTrackAnnotations,
        );
        setSkipFailedCheck(Boolean(
            options.skipFailedCheck !== undefined ? options.skipFailedCheck : DEFAULTS.skipFailedCheck,
        ));
    }, [open, sourceId, corpus.data, corpusId]);

    const mutation = useMutation(startSuite, {
        onSuccess: (data) => {
            queryClient.invalidateQueries('test-runs');
            onStarted(data.id || data._id);
        },
        onError: (err) => setError(err.message),
    });

    const submit = () => {
        setError('');
        const mails = (corpus.data && corpus.data.mails) || [];
        const needsCargo = mails.some(mail => mail.annotationType === 'cargo');
        const needsVessel = mails.some(mail => mail.annotationType === 'vessel');
        if (!corpusId) {
            setError('Select a corpus');
            return;
        }
        if (needsCargo && !cargoImageTag) {
            setError('Select a cargo image');
            return;
        }
        if (needsVessel && !vesselImageTag) {
            setError('Select a vessel image');
            return;
        }
        const gaz = gazetteerRevision.trim();
        mutation.mutate({
            corpusId,
            cargoImageTag: needsCargo ? cargoImageTag : undefined,
            vesselImageTag: needsVessel ? vesselImageTag : undefined,
            gazetteerRevision: gaz || undefined,
            reason,
            replicas: Number(replicas) || undefined,
            timeout: Number(timeout) || undefined,
            memoryReservationGb: Number(memoryReservationGb) || undefined,
            japeLimitsEnabled,
            japeTrackAnnotations,
            skipFailedCheck,
        });
    };

    const cargoChoices = imageItems(cargoModels.data, cargoImageTag);
    const vesselChoices = imageItems(vesselModels.data, vesselImageTag);

    return (
        <Dialog
            title={source ? 'New run from this' : 'Start recognition test'}
            open={open}
            onRequestClose={onClose}
            autoScrollBodyContent
            actions={[
                <FlatButton key="cancel" label="Cancel" onClick={onClose} />,
                <FlatButton key="run" label="Start" primary disabled={mutation.isLoading} onClick={submit} />,
            ]}
        >
            {error && <div className="test-runs-error">{error}</div>}
            {(corpora.error || cargoModels.error || vesselModels.error || corpus.error) && (
                <div className="test-runs-error">
                    {(corpora.error || cargoModels.error || vesselModels.error || corpus.error).message}
                </div>
            )}
            <SelectField
                floatingLabelText="Corpus"
                value={corpusId}
                onChange={(e, i, value) => setCorpusId(value)}
                fullWidth
            >
                {(Array.isArray(corpora.data) ? corpora.data : []).map((item) => (
                    <MenuItem
                        key={item._id}
                        value={item._id}
                        primaryText={`${item.name} (${item.mailCount} mails)`}
                    />
                ))}
            </SelectField>
            {(!corpusId || ((corpus.data && corpus.data.mails) || []).some(mail => mail.annotationType === 'cargo')) && (
            <SelectField
                floatingLabelText="Cargo image"
                value={cargoImageTag}
                onChange={(e, i, value) => setCargoImageTag(value)}
                fullWidth
            >
                {cargoChoices.map((item) => (
                    <MenuItem key={item.image} value={item.image} primaryText={item.image} />
                ))}
            </SelectField>
            )}
            {(!corpusId || ((corpus.data && corpus.data.mails) || []).some(mail => mail.annotationType === 'vessel')) && (
            <SelectField
                floatingLabelText="Vessel image"
                value={vesselImageTag}
                onChange={(e, i, value) => setVesselImageTag(value)}
                fullWidth
            >
                {vesselChoices.map((item) => (
                    <MenuItem key={item.image} value={item.image} primaryText={item.image} />
                ))}
            </SelectField>
            )}
            <TextField
                floatingLabelText="Gazetteer revision"
                hintText="Branch or commit. Empty pins master."
                value={gazetteerRevision}
                onChange={(e, value) => setGazetteerRevision(value)}
                fullWidth
            />
            <TextField
                floatingLabelText="Reason"
                value={reason}
                onChange={(e, value) => setReason(value)}
                fullWidth
            />
            <TextField
                floatingLabelText="Replicas"
                value={replicas}
                onChange={(e, value) => setReplicas(value)}
                fullWidth
            />
            <TextField
                floatingLabelText="Per-document timeout (seconds)"
                value={timeout}
                onChange={(e, value) => setTimeoutSec(value)}
                fullWidth
            />
            <TextField
                floatingLabelText="Memory reservation (GB)"
                value={memoryReservationGb}
                onChange={(e, value) => setMemoryReservationGb(value)}
                fullWidth
            />
            <Checkbox
                label="japeLimitsEnabled"
                checked={japeLimitsEnabled}
                onCheck={(e, checked) => setJapeLimitsEnabled(checked)}
                style={{ marginTop: 12 }}
            />
            <Checkbox
                label="japeTrackAnnotations"
                checked={japeTrackAnnotations}
                onCheck={(e, checked) => setJapeTrackAnnotations(checked)}
            />
            <Checkbox
                label="skipFailedCheck"
                checked={skipFailedCheck}
                onCheck={(e, checked) => setSkipFailedCheck(checked)}
            />
        </Dialog>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/StartDialog.jsx